import type { InjectionKey } from "vue";
import mediaObjectApi from "@apis/media.object.api";
import { usePdfCaptureStore } from "@plugins/authoring-copilot/pdf-capture.store.ts";
import { isEmpty } from "@utils/var.check.ts";

export interface PdfViewerOpenPayload {
  uuid: string;
  filename?: string;
  contextType?: string;
  contextUuid?: string;
}

export type PdfViewerOpenFn = (payload: PdfViewerOpenPayload) => Promise<void>;

/**
 * Injection key used to override the PDF viewer open behaviour.
 * AuthoringCoPilotKnowledgeBase provides its own implementation that
 * adds KB capture context; all other consumers get the view-only default.
 */
export const PDF_VIEWER_OPEN_KEY: InjectionKey<PdfViewerOpenFn> = Symbol("pdfViewerOpen");

/**
 * Default (view-only) implementation — fetches the PDF URL then opens
 * the global PdfViewerCaptureOverlay without any KB capture context.
 */
export function useDefaultPdfViewerOpen(): PdfViewerOpenFn {
  const store = usePdfCaptureStore();

  return async ({ uuid, filename, contextType, contextUuid }: PdfViewerOpenPayload) => {
    try {
      const viewPdfParams: Record<string, string> = { uuid };
      if (contextUuid) viewPdfParams.contextUuid = contextUuid;
      if (contextType) viewPdfParams.contextType = contextType;

      const response = await mediaObjectApi.viewPdf(viewPdfParams);
      if (!response?.data) return;

      store.open({
        pdfUrl: String(response.data),
        pdfFilename: isEmpty(filename) ? uuid : filename!,
        mediaObjectUuid: uuid,
        // No KB context → view-only mode; capture buttons hidden in overlay
      });
    } catch {
      // Silently ignore — no toast context available at this layer
    }
  };
}
